(() => {
  const euSizes = Array.from({ length: 20 }, (_, index) => 42 + index * 2);
  const usSizes = ['2', '3', '3 3/4', '4 1/2', '5 1/4', '6', '6 3/4', '7 1/2', '8 1/2', '9', '10', '10 3/4', '11 1/2', '12 1/4', '13', '13 3/4', '14 1/2', '15', '15 1/2', '16'];
  const ukSizes = ['D', 'F', 'H', 'I 1/2', 'J 1/2', 'L 1/2', 'N', 'P', 'Q 1/2', 'S', 'T', 'V', 'W 1/2', 'Y 1/2', 'Z 1/2', 'Z+2', 'Z+3', 'Z+4', 'Z+5', 'Z+6'];
  const diameterUpper = [13.37, 14.01, 14.64, 15.28, 15.92, 16.55, 17.19, 17.83, 18.46, 19.10, 19.74, 20.37, 21.01, 21.65, 22.28, 22.92, 23.55, 24.19, 24.83, 25.46];

  const number = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    return /^\d+(?:\.\d+)?$/.test(normalized) ? Number(normalized) : NaN;
  };

  const estimate = (raw, method, audience) => {
    const value = number(raw);
    if (!Number.isFinite(value)) return { state: 'invalid' };
    if (!['women', 'men'].includes(audience) || !['circumference', 'diameter'].includes(method)) return { state: 'invalid' };
    const first = audience === 'men' ? 5 : 0;
    const last = audience === 'men' ? 19 : 14;
    const min = method === 'diameter' ? (audience === 'men' ? 15.93 : 12.70) : (audience === 'men' ? 50 : 40);
    const max = method === 'diameter' ? diameterUpper[last] : euSizes[last];
    if (value < min || value > max) return { state: 'outside' };
    const index = method === 'diameter'
      ? diameterUpper.findIndex((upper, i) => i >= first && value <= upper)
      : euSizes.findIndex((upper, i) => i >= first && value <= upper);
    return { state: 'result', eu: euSizes[index] };
  };

  const market = (country) => {
    if (['US', 'CA'].includes(country)) return 'us';
    if (['GB', 'AU'].includes(country)) return 'uk';
    if (['FR', 'IT'].includes(country)) return 'it';
    return 'eu';
  };

  const label = (eu, system) => {
    const index = euSizes.indexOf(eu);
    if (index < 0) return '';
    if (system === 'us') return usSizes[index];
    if (system === 'uk') return ukSizes[index];
    if (system === 'it') return String(eu - 40);
    return String(eu);
  };

  window.HolzkernRingSizing = { number, estimate, market, label };
  if (customElements.get('ring-size-guide')) return;

  customElements.define('ring-size-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.method = 'circumference';
      this.input = this.querySelector('[data-ring-input]');
      this.error = this.querySelector('[data-ring-error]');
      this.entry = this.querySelector('[data-ring-entry]');
      this.result = this.querySelector('[data-ring-result]');
      this.help = this.querySelector('[data-ring-help]');
      this.outside = this.querySelector('[data-ring-outside]');
      this.applyButton = this.querySelector('[data-ring-apply]');
      this.unavailable = this.querySelector('[data-ring-unavailable]');
      this.recommendations = this.querySelector('[data-ring-recommendations]');
      this.browseButton = this.querySelector('[data-ring-browse]');
      this.categories = this.querySelector('[data-ring-categories]');
      this.querySelector('[data-ring-calculate]').addEventListener('click', () => this.calculate());
      this.applyButton.addEventListener('click', () => this.apply());
      this.querySelectorAll('[data-ring-retry]').forEach((button) => button.addEventListener('click', () => this.reset()));
      this.querySelectorAll('[data-ring-method]').forEach((button) => button.addEventListener('click', () => this.changeMethod(button.dataset.ringMethod)));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') { event.preventDefault(); this.calculate(); }
      });
      this.input.addEventListener('input', () => {
        this.error.hidden = true;
        this.input.removeAttribute('aria-invalid');
      });
    }

    disconnectedCallback() {
      this.request?.abort();
    }

    changeMethod(method) {
      this.method = method;
      this.querySelectorAll('[data-ring-method]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.ringMethod === method)));
      this.querySelector('[data-ring-input-label]').textContent = this.dataset[`${method}Label`];
      this.querySelectorAll('[data-ring-instructions]').forEach((panel) => { panel.hidden = panel.dataset.ringInstructions !== method; });
      this.reset();
    }

    reset() {
      this.request?.abort();
      this.recommendedSize = null;
      this.entry.hidden = false;
      this.result.hidden = true;
      this.outside.hidden = true;
      this.error.hidden = true;
      this.help.hidden = false;
      if (this.categories) this.categories.hidden = true;
      this.input.removeAttribute('aria-invalid');
      this.input.focus();
    }

    calculate() {
      const result = estimate(this.input.value, this.method, this.dataset.audience);
      this.request?.abort();
      this.recommendedSize = null;
      this.result.hidden = true;
      this.outside.hidden = true;
      this.error.hidden = true;
      if (this.categories) this.categories.hidden = true;
      this.input.removeAttribute('aria-invalid');
      if (result.state === 'invalid') {
        this.error.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        return;
      }
      this.entry.hidden = true;
      if (result.state === 'outside') {
        this.outside.hidden = false;
        this.help.hidden = false;
        this.revealCategories();
        this.outside.focus();
        return;
      }
      this.recommendedSize = result.eu;
      const system = market(this.dataset.country);
      this.querySelector('[data-ring-result-size]').textContent = label(result.eu, system);
      this.querySelector('[data-ring-result-system]').textContent = this.dataset[`${system}Label`];
      this.result.hidden = false;
      this.help.hidden = true;
      this.result.focus();
      this.showAvailability();
    }

    controlForSize() {
      const picker = document.getElementById(this.dataset.pickerId);
      if (!picker || !this.recommendedSize) return null;
      const name = this.dataset.optionName;
      const position = this.dataset.optionPosition;
      const controls = [...picker.querySelectorAll('input[type="radio"], option')];
      return controls.find((control) => {
        const sizeControl = control.tagName === 'OPTION'
          ? control.parentElement.name.trim() === `options[${name}]`
          : control.name.trim() === `${name}-${position}`;
        return sizeControl && number(control.value) === this.recommendedSize
          && !control.disabled && !control.classList.contains('disabled');
      }) ?? null;
    }

    showAvailability() {
      const available = Boolean(this.controlForSize());
      this.applyButton.hidden = !available;
      this.unavailable.hidden = available;
      this.recommendations.hidden = true;
      if (!available) {
        this.querySelector('[data-ring-other-heading]').textContent = this.dataset.otherHeading.replace('{{ size }}', label(this.recommendedSize, market(this.dataset.country)));
        this.browseButton.hidden = true;
        this.browseButton.textContent = this.dataset.browseLabel.replace('{{ size }}', label(this.recommendedSize, market(this.dataset.country)));
        const url = new URL(this.dataset.collectionUrl, window.location.origin);
        url.searchParams.set('filter.v.option.ring size', String(this.recommendedSize));
        url.searchParams.set('filter.v.availability', '1');
        this.browseButton.href = url.pathname + url.search;
        this.loadAlternatives(url);
      }
    }

    async loadAlternatives(collectionUrl) {
      const size = this.recommendedSize;
      const request = this.request = new AbortController();
      const cards = this.querySelector('[data-ring-cards]');
      this.browseButton.hidden = true;
      cards.replaceChildren();
      try {
        collectionUrl.searchParams.set('section_id', 'ring-size-recommendations');
        for (let page = 1; page <= 5 && cards.children.length < 4; page++) {
          collectionUrl.searchParams.set('page', String(page));
          const response = await fetch(collectionUrl.pathname + collectionUrl.search, { signal: request.signal });
          if (!response.ok) throw new Error('Recommendations unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (request.signal.aborted || this.recommendedSize !== size) return;
          if (page === 1) this.browseButton.hidden = Number(html.querySelector('[data-ring-candidate-page]')?.dataset.total || 0) === 0;
          const candidates = html.querySelectorAll('[data-ring-candidate]');
          for (const candidate of candidates) {
            if (cards.children.length >= 4) break;
            if (candidate.dataset.productId === this.dataset.productId || [...cards.children].some((card) => card.dataset.productId === candidate.dataset.productId)) continue;
            const variants = JSON.parse(candidate.querySelector('[data-ring-variants]').textContent);
            const variant = variants.find((item) => number(item.size) === size);
            if (!variant) continue;
            const card = candidate.cloneNode(true);
            card.querySelector('[data-ring-variants]').remove();
            const link = card.querySelector('a');
            const url = new URL(link.getAttribute('href'), window.location.origin);
            url.searchParams.set('variant', variant.id);
            link.href = url.pathname + url.search;
            card.querySelector('[data-ring-price]').textContent = variant.price;
            cards.appendChild(card);
          }
          if (html.querySelector('[data-ring-candidate-page]')?.dataset.hasNext !== 'true') break;
        }
        if (request.signal.aborted || this.recommendedSize !== size) return;
        this.recommendations.hidden = cards.children.length === 0;
        if (cards.children.length === 0) this.revealCategories();
        else if (this.categories) this.categories.hidden = true;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.browseButton.hidden = true;
          this.revealCategories();
        }
      }
    }

    revealCategories() {
      if (!this.categories) return;
      this.categories.hidden = false;
      requestAnimationFrame(() => {
        this.categories.querySelector('splide-carousel')?.slideInstance?.refresh?.();
      });
    }

    apply() {
      const control = this.controlForSize();
      if (!control) { this.showAvailability(); return; }
      if (control.tagName === 'OPTION') {
        control.parentElement.value = control.value;
        control.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        control.checked = true;
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.closest('modal-dialog')?.hide();
    }
  });
})();
